// McDeal — single screen for one MC deal's full lifecycle.
//
// All 3 stages live here, vertically. As each stage completes, its panel shows
// a ✓ summary; the next stage's panel becomes interactive.
//
//   ─── Stage 1 ─── Book (deposit)
//   ─── Stage 2 ─── Pay seller
//   ─── Stage 3 ─── Deliver (customer balance + cheque hand-off)
//   ─── Void ───
//
// Reached via:
//   - tap a row in the Open cheques card on Home
//   - redirect from /mc after booking
//   - direct URL /mc/<dealId>
//
// 14 Sept 2026, the minimalist pass (SWEEP-SCOPE §5.4, §6, §7, item 44): the
// deliver intro, the INR rate note and the Adjust-balance sentence live in
// "About this screen"; "← Back"; the boxes read in BoxLines' words with no
// colour; one w-24 asset box everywhere; "Pay the remaining" is a link under
// the row; the amount label is always drawn; stage 3 is labelled Amount /
// Rate / Total in AED like stage 2; fees are percent with "= N bps" beside on
// both stages; Cancel is a link; the width cap the other screens use. The
// meter, the arithmetic lines, the two-tap completion, the settled summary
// and the void panel stay.
//
// 19 Sept 2026, the phone pass (375–430px, below lg; lg+ unchanged): the
// reference in the subtitle is short and copyable (shortReference / CopyRef,
// as Settlements and the deal panel print it — §6 "no raw ids") and closes
// the line; every action button is 40px tall on the phone; the refusal's two
// buttons stack full width; the "Take customer money in" door stands on its
// own line; the link-buttons carry a tap-high padding; the void reason and
// the notes read at 13px. The fields were already full width with the w-24
// asset box beside them, so the forms keep their shape.
//
// 21 Oct 2026 audit (operator: "I need clean view for operator"): a done stage
// is ONE line (title, where and when, ✓ done) and on a settled deal carries
// no figure the summary card already holds; the rails read as the pickers name
// them ("Hot wallet · TRC", not the slug's "Hot · TRC"); the fee boxes read
// "0.40" at the booking screen's column width; stage 3 opens with its button
// saying only "Confirm delivery" until a 0 is typed, with Rate above Amount;
// the arithmetic lines say each figure once. What posts, what enables a button
// and every refusal are as they were.
//
// 22 Oct 2026 design-system sweep: the boxes (header, "still to receive", stage 3's prepaid figure), the accounts and the deal
// itself each say when they could not be read — an unread box counted as nothing and the suggested balance was too high without
// a word — a refresh that fails keeps the last good read under one amber line; stage 3's 22-word arithmetic line is two and does
// not print "still owed" before the deposit is valued; the delivery refusal and the void are one amber line each with the
// route's own sentence behind Details and both ways out kept; one line per party in the header; the notes and a void reason break
// inside a word that does not fit (a 66-character transaction hash was cut off at the phone's edge).
//
// Astra round 1 + the design system's Appendix B (23 Oct 2026): a figure is drawn from a party's box only out of a read that
// SUCCEEDED whole (useBoxes) — not from one that failed, not from a refresh that failed over cached rows, not from an HTTP 200
// that said `balances_read: false`, not from a first read still waiting (opened offline it is PAUSED, not empty), and not from a
// roster that does not hold the party at all (it is the first 200 active parties alphabetically: the rest are asked for by name).
// The header's line owns the failure and its Try again; "still to receive" and stage 3 draw nothing of their own about it, and
// stage 3 suggests no balance and projects no verdict. A deal read that is still waiting shows the waiting line.

//
// The rebuilt page (screens/v2/ChequeProgress.tsx, behind uiV2(5)) mounts PaySellerInline, DeliverInline and VoidInline
// from here as they are, and reads the customer's box and what is still to receive through useBoxes and stillToReceive —
// exported for that, one copy of the press, its rules and the figure.
import { useState, useMemo, useEffect, useLayoutEffect, useRef, Children, type ReactNode } from 'react';
import { errorWords, readWords, ApiError } from '../lib/errors';
import { BALANCES_UNREAD, balancesUnread } from '../lib/balances-read';
import { Pending } from '../components/Pending';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, type McDeliveryStarted, type McUncountedLeg } from '../lib/api';
import { tg } from '../lib/telegram';
import {
  NOT_SAVED_MORE, answerOf, askForCheque, countedSettles, dealKey, deliveryBody, finishDeliveryBody, finishLegBody, mintLegKey, presses, sellerLegBody, tryBegin, useHeld, useReconcile,
  type PressScope,
  type DeliveryBody, type Held, type SellerLegBody,
} from '../lib/chequePress';
import { displayAsset, fmtAmount, fmtRelative, fmtRateExact, shownAmount } from '../lib/format';
import { ledgerScale, atLedger } from '../lib/ledger-scale';
import { textPx } from '../lib/glyph-width';
import { partyName, railName, foldAsset, sameAsset, dustFor, shortReference, partyBalanceOf } from '../lib/naming';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { ScreenHeader } from '../components/ScreenHeader';
import { Field } from '../components/Field';
import { ReadFailed, ServerLine, StateLine, TAP } from '../components/kit';
import { KeyValue } from '../components/KeyValue';
import { CopyRef } from '../components/CopyRef';
import { boxWords } from '../components/BoxLines';
import { MC_PAY_OUT_KINDS, FeePctField } from './ManagerCheque';
import { useTradeMath } from '../hooks/useTradeMath';
import { fmtQty } from '../lib/trade-math';
import { isFiat } from '../lib/workflow';

// THIS SCREEN MUST ROUND THE WAY THE ROUTE ROUNDS.
// Every AED figure stage 3 posts goes through `divHalfEven` in
// bff/src/routes/manager-cheque.ts — half-even, on minor units. Math.round is
// half-up, so a figure landing exactly on half a fils came out a fils apart
// from what was actually posted: the screen showed a clean close and the
// difference sat on the customer's box. Two decimals, because AED is a 2dp
// asset. It lives beside its only callers rather than in lib/format.ts, which
// is another agent's file this build.
function aedHalfEven(value: number): number {
  if (!Number.isFinite(value)) return 0;
  // THE TIE MUST BE FOUND IN INTEGERS. Testing `value * 100 - floor !== 0.5`
  // asks a float whether it is exactly a half, which for a genuine tie such as
  // 3,625,943.75 at 184 bps it is not — so the tie fell through to Math.round,
  // which rounds half UP, the very thing this helper exists to avoid. Scaling by
  // 1000 and reading the last digit answers the same question exactly.
  const milli = Math.round(value * 1000);          // fils × 10, exact for money
  const fils  = Math.trunc(milli / 10);
  const rem   = Math.abs(milli % 10);
  if (rem !== 5) return Math.round(value * 100) / 100;
  const away = fils + (milli < 0 ? -1 : 1);        // the neighbour away from zero
  return (Math.abs(fils % 2) === 0 ? fils : away) / 100;
}

/** The one asset box beside an amount — w-24 everywhere on this page (§5.4). */
function AssetBox({ asset }: { asset: string }) {
  return (
    <div className="w-24 shrink-0 input bg-tg-bg/30 text-tg-hint text-center tabular text-body">
      {asset ? displayAsset(asset) : '—'}
    </div>
  );
}

/** The fee box's width: the booking screen's column — a third of its 720px page,
 *  232px — from lg (21 Oct 2026 audit: here it ran 880px wide for "0.40"). On
 *  the phone it stays the full line, as every other field on the page is. */
const FEE_FIELD_WIDTH = 'lg:max-w-[232px]';

/** The delivery button's words (21 Oct 2026 audit). Stage 3 opens on an EMPTY
 *  Amount, and Number('') is 0 — so the button read "nothing owed at handover"
 *  over a form nobody had filled in (it was disabled, but said so). That
 *  wording is for an Amount that READS 0; an empty (or unreadable) one is just
 *  "Confirm delivery". Words only: when the button is enabled is the form's
 *  own rule (an empty Amount keeps it off) and is not decided here. */
export function deliverWords(pending: boolean, amount: string): string {
  if (pending) return 'Posting…';
  return amount.trim() !== '' && Number(amount) === 0
    ? 'Confirm delivery — nothing owed at handover'
    : 'Confirm delivery';
}

export function McDeal() {
  const { dealId } = useParams<{ dealId: string }>();
  const nav = useNavigate();
  const qc  = useQueryClient();

  const q = useQuery({
    queryKey: dealKey(dealId ?? ''),
    queryFn:  () => api.getMcDeal(dealId!),
    enabled:  !!dealId,
  });
  // The desk's accounts, for the names the pickers use (21 Oct 2026 audit: the
  // done stages named the rails from the slug alone — "Hot · TRC" beside the
  // pickers' "Hot wallet · TRC"). The same read the pay-seller form makes, so
  // one fetch. A hook, so it stays above the data guards below.
  const treasury = useQuery({ queryKey: ['treasury', 'balances'], queryFn: () => api.treasuryBalances('dubaiotc') });
  // A held press is over once its cheque has moved on (the seller paid, the delivery done, voided): its entry on the disk goes with it (lib/chequePress.ts).
  const stageNow = q.data?.deal.lifecycle;
  useEffect(() => {
    if (!dealId || !stageNow) return;
    if (stageNow !== 'mc_pending_seller') presses.drop('seller-leg', dealId);
    if (stageNow !== 'mc_seller_paid') presses.drop('delivery', dealId);
  }, [dealId, stageNow]);

  // A kept seller press the cheque's meter already COUNTS is settled — no tap, no request: the books list the keys they hold (mc_counted_legs), and the press was waiting only to learn this. A
  // press with a request out is left alone (its own answer settles it). The field is absent on a Worker before it was added: then nothing happens and "Send again" is the way.
  const countedNow = q.data?.deal.mc_counted_legs;
  // The press is READ LIVE (useHeld): a counted list that arrived while the request was still out must be acted on when the request fails and the press turns into one that waits — the list
  // itself (identical on a refetch) will not change again to say so.
  const sellerPress = useHeld<SellerLegBody>('seller-leg', dealId ?? '');
  useEffect(() => {
    if (!dealId || !countedNow) return;
    if (countedSettles(sellerPress, countedNow)) presses.answered('seller-leg', dealId, 'done', sellerPress!.body);
  }, [dealId, countedNow, sellerPress]);

  // PENDING, not isLoading: a first read opened OFFLINE is paused — no data, not fetching — and is still waiting.
  if (q.isPending) return <Pending />;
  // Only replace the screen when there is NOTHING to show. A background refetch
  // that fails used to unmount the stage forms with half-typed figures in them
  // (3 Sept audit); now it shows a line above the deal and leaves the work alone.
  if (q.isError && !q.data) {
    return (
      <div className="space-y-3 lg:max-w-[720px] lg:mx-auto">
        <button onClick={() => nav('/')} className="btn-link text-meta">← Back</button>
        <ReadFailed what="this cheque" error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );
  }

  const d = q.data!.deal;
  const lifecycle      = d.lifecycle;
  const isVoided       = lifecycle === 'voided';
  const isPendingSeller = lifecycle === 'mc_pending_seller';
  const isSellerPaid   = lifecycle === 'mc_seller_paid';
  const isSettled      = lifecycle === 'settled';

  const face        = Number(d.mc_face_amount);
  const custFeeBps  = Number(d.mc_customer_fee_bps);
  const selFeeBps   = Number(d.mc_seller_fee_bps);
  const depAmt      = Number(d.mc_deposit_amount);
  const customerFee = face * custFeeBps / 10000;
  const sellerFee   = face * selFeeBps  / 10000;
  const spread          = customerFee - sellerFee;

  // THE BALANCE CARRIES ITS OWN ASSET. `admin.deals` stores mc_balance_amount
  // as a bare number with no column beside it for the asset, and delivery will
  // take the balance in a different asset from the deposit — a USDT deposit
  // closed with an AED balance is the shape the probe rows have. The route now
  // reads the asset back off the delivery's own audit row and sends it as
  // mc_balance_asset; lib/api.ts is another agent's file this build and does
  // not declare the field yet, so it is widened here and falls back to the
  // booking asset for deals delivered before the route sent it.
  const balanceAsset = (d as typeof d & { mc_balance_asset?: string | null }).mc_balance_asset ?? d.asset_take;

  const refresh = () => qc.invalidateQueries({ queryKey: ['mc'] });
  // The stage asks for THIS cheque again after a press ends — the exact cheque, subscribed or not (lib/chequePress.ts askForCheque).
  const ask = () => void askForCheque(qc, dealId ?? '');
  const customer = partyName({ display_name: d.customer_name, slug: d.customer_slug });
  const seller   = partyName({ display_name: d.mc_seller_display_name, slug: d.mc_seller_slug });
  // An account as the pickers name it: the stored label ("Hot wallet · TRC") when
  // the desk's accounts have been read, else what the slug alone makes of it.
  // `asset` narrows to one row where the stage knows it; the seller's last leg
  // does not (its asset column is the literal 'AED'), and one account has one
  // label across its assets.
  const railRows = treasury.data?.rows ?? [];
  const accountName = (slug: string | null | undefined, asset?: string) =>
    railName((slug ? railRows.find((r) => r.slug === slug && (!asset || sameAsset(r.asset, asset))) : undefined) ?? { slug });

  return (
    <div className="space-y-3 lg:max-w-[720px] lg:mx-auto lg:space-y-4">
      <button onClick={() => nav('/')} className="btn-link text-meta">← Back</button>
      {q.isError && (
        <StateLine
          tone="warn"
          more={readWords(q.error)}
          action={<button type="button" className={TAP} onClick={() => void q.refetch()}>Try again</button>}
        >
          Books didn&apos;t answer — showing the last read.
        </StateLine>
      )}

      {/* The reference closes the subtitle, as Settlements and Money movements
          place it: CopyRef keeps its copy icon's width even when unhovered,
          and mid-sentence that read as a blank gap before the first separator
          on the phone (19 Sept 2026 review). */}
      <ScreenHeader
        title={customer}
        subtitle={<>{fmtAmount(face, 'AED')} AED face · seller {seller} · <CopyRef value={d.deal_id} label={shortReference(d.deal_id)} /></>}
        help={
          <>
            <p>
              Stage 2 pays the seller in legs — cash, coins at an agreed rate, more than one account — until
              the meter reads paid; a leg within a dirham of the figure closes the stage by itself. Stage 3
              hands the cheque over: the customer pays whatever is still owed at handover — 0 if they already
              paid in full — and walks out with the cheque.
            </p>
            <p>
              Coins are valued at the rate you type, the pair&rsquo;s way (AED per USDT, INR per AED). On an
              INR deposit the books carry the inverse to eight places, so ten million rupees can value a dirham apart
              from the box — the closing line is worked the books&rsquo; way.
            </p>
            <p>
              Money in before money out: record each of the customer&rsquo;s tranches from Record money as it
              lands and pay the seller from it. Voiding marks the deal and moves nothing — what is posted stays
              posted. Give a deposit back from Record money; Adjust balance corrects the books and hands no
              money over.
            </p>
          </>
        }
      >
        <BoxesNow customerSlug={d.customer_slug} sellerSlug={d.mc_seller_slug} />
        {/* MONEY IN BEFORE MONEY OUT (operator, 10 Sept 2026: a 1.49 crore
            cheque, 3 lakh deposit, and the seller leg refused for lack of
            cash). The customer's tranches are ordinary receipts on their box;
            this is the same Record money screen, pre-filled for this customer,
            so the desk takes the money in first and pays the seller from it. */}
        {(lifecycle === 'mc_pending_seller' || lifecycle === 'mc_seller_paid') && (
          <CustomerStillOwes
            customerSlug={d.customer_slug}
            custAsset={d.asset_take}
            owedAed={face + customerFee}
            href={`/record?party=${encodeURIComponent(d.customer_slug)}&dir=in`
              + (d.mc_customer_treasury_slug ? `&rail=${encodeURIComponent(d.mc_customer_treasury_slug)}&railAsset=${encodeURIComponent(d.asset_take)}` : `&asset=${encodeURIComponent(d.asset_take)}`)
              + `&note=${encodeURIComponent('cheque ' + d.deal_id)}`}
          />
        )}
        {/* A delivery charge on this cheque (2 Oct 2026) is EXTRA, on top of
            the cheque fee, and is its own entry on its own profit line — the
            fee and the stages below are untouched by it. */}
        <p className="text-meta">
          <Link className="btn-link max-lg:block max-lg:py-1" to={`/record?mode=charge&party=${encodeURIComponent(d.customer_slug)}&deal=${encodeURIComponent(d.deal_id)}`}>
            Add delivery charge →
          </Link>
        </p>
        <ProgressBar lifecycle={lifecycle} />
      </ScreenHeader>

      {/* Stage 1 — Booked. On a settled deal the summary card below holds the
          deposit figure, so the line says only where and when (21 Oct 2026). */}
      <Panel
        n={1}
        title="Book deposit"
        done
        sub={
          <>
            {!isSettled && <>{fmtAmount(depAmt, d.asset_take)} {displayAsset(d.asset_take)} </>}
            into {accountName(d.mc_customer_treasury_slug, d.asset_take)} · {fmtRelative(d.booked_at)}
          </>
        }
      />

      {/* Stage 2 — Pay seller.
          WHAT THE ROW ACTUALLY KNOWS ABOUT THE SELLER'S PAYMENT. There is one
          meter, one asset column and one rail column, and stage 2 may post any
          number of legs into them: mc_seller_pay_amount is the running
          AED-EQUIVALENT (each leg added at its own agreed rate),
          mc_seller_pay_asset is written as the literal 'AED' whatever went out
          of the door, and mc_seller_pay_treasury_slug is overwritten by
          whichever rail the last leg used. GRYPTO's seller took 10,50,000 AED
          in cash and the rest in USDT at 3.676, and this line read the whole
          sum as AED "from" the USDT wallet. Say only what is known. */}
      {!isVoided && (
        <Panel
          n={2}
          title="Pay seller"
          done={isSellerPaid || isSettled}
          sub={(isSellerPaid || isSettled) ? (
            <>
              {/* On a settled deal the card below holds the seller's figure. */}
              {!isSettled && <>{fmtAmount(Number(d.mc_seller_pay_amount), 'AED')} AED-equivalent</>}
              {d.mc_seller_pay_treasury_slug
                ? <>{!isSettled && ' · '}last leg from {accountName(d.mc_seller_pay_treasury_slug)}</>
                : null}
              {d.mc_seller_paid_at ? <>{(!isSettled || d.mc_seller_pay_treasury_slug) && ' · '}{fmtRelative(d.mc_seller_paid_at)}</> : null}
            </>
          ) : null}
        >
          {isPendingSeller && (
            <PaySellerInline
              key={d.deal_id}
              dealId={d.deal_id}
              face={face}
              defaultFeeBps={selFeeBps}
              paidSoFar={Number(d.mc_seller_pay_amount ?? 0)}
              sellerSlug={d.mc_seller_slug}
              uncounted={d.mc_uncounted_legs ?? []}
              readTick={q.data?.readTick ?? 0}
              reading={q.isFetching}
              readError={q.isError ? q.error : null}
              onRetry={() => void q.refetch()}
              onAsk={ask}
              onDone={refresh}
            />
          )}
        </Panel>
      )}

      {/* Stage 3 — Deliver */}
      {!isVoided && (
        <Panel
          n={3}
          title="Deliver to customer"
          done={isSettled}
          // The balance is in the card below and the cheque in the heading.
          sub={isSettled && d.mc_delivered_at ? fmtRelative(d.mc_delivered_at) : null}
        >
          {isSellerPaid && (
            <DeliverInline
              key={d.deal_id}
              dealId={d.deal_id}
              face={face}
              defaultFeeBps={custFeeBps}
              deposit={depAmt}
              custAsset={d.asset_take}
              customerSlug={d.customer_slug}
              started={d.mc_delivery_started ?? null}
              readTick={q.data?.readTick ?? 0}
              reading={q.isFetching}
              readError={q.isError ? q.error : null}
              onRetry={() => void q.refetch()}
              onAsk={ask}
              onDone={refresh}
            />
          )}
          {isPendingSeller && (
            <p className="text-meta text-tg-hint">Pay the seller first to unlock delivery.</p>
          )}
        </Panel>
      )}

      {/* Summary card when settled.
          THE DEPOSIT AND THE BALANCE ARE TWO FIGURES, NOT ONE. This added them
          and labelled the sum with the booking asset, so a deposit taken in
          USDT and a balance settled in AED read as a single USDT total the
          desk never received. Each line now shows a figure the books actually
          hold, beside the asset it was taken in. No colour on the spread —
          the word and the figure carry it (§7 rule 10). */}
      {isSettled && (
        <div className="card-tight">
          <KeyValue rows={[
            { label: 'Deposit',  hint: 'customer paid', value: `${fmtAmount(depAmt, d.asset_take)} ${displayAsset(d.asset_take)}` },
            { label: 'Balance',  hint: 'customer paid', value: `${fmtAmount(Number(d.mc_balance_amount), balanceAsset)} ${displayAsset(balanceAsset)}` },
            { label: 'Seller',   hint: 'desk paid',     value: `${fmtAmount(Number(d.mc_seller_pay_amount), 'AED')} AED-equivalent` },
            { label: 'Spread',                          value: `${fmtAmount(d.spread_amount ?? spread, 'AED')} AED` },
          ]} />
        </div>
      )}

      {/* Void */}
      {!isVoided && !isSettled && (
        <VoidInline
          dealId={d.deal_id}
          deposit={depAmt}
          depositAsset={d.asset_take}
          sellerPaidAed={Number(d.mc_seller_pay_amount ?? 0)}
          onDone={refresh}
        />
      )}

      {/* The void reason and the notes are operator-written content, so they read at the body size on the phone (the meta size
          is for hints); lg+ keeps the meta size. They break inside a word that does not fit: a Chain row writes "chain 0x…"
          into the notes, and a full transaction hash (66 characters, no break in it) ran 520px in a 300px card and was cut
          off at the phone's edge (22 Oct 2026 sweep). */}
      {isVoided && (
        <div className="card-tight text-body lg:text-meta text-tg-hint break-words">
          ✗ Voided · {d.void_reason ?? ''}
          {d.voided_at && <> · {fmtRelative(d.voided_at)}</>}
        </div>
      )}

      {d.notes && (
        <div className="card-tight text-meta">
          <p className="text-tg-hint mb-0.5">Notes</p>
          <p className="text-body lg:text-meta break-words">{d.notes}</p>
        </div>
      )}
    </div>
  );
}

// ── visual: progress bar (●─○─○) ────────────────────────────────────────
function ProgressBar({ lifecycle }: { lifecycle: string }) {
  const s1 = true;
  const s2 = lifecycle === 'mc_seller_paid' || lifecycle === 'settled';
  const s3 = lifecycle === 'settled';
  const voided = lifecycle === 'voided';
  return (
    <div className="flex items-center gap-1 text-meta">
      <Dot done={s1} voided={voided} /> <span className="text-tg-hint">book</span>
      <Line />
      <Dot done={s2} voided={voided} /> <span className="text-tg-hint">pay seller</span>
      <Line />
      <Dot done={s3} voided={voided} /> <span className="text-tg-hint">deliver</span>
    </div>
  );
}
const Dot = ({ done, voided }: { done?: boolean; voided?: boolean }) => (
  <span className={`inline-block w-2 h-2 rounded-full ${
    voided ? 'bg-neg' : done ? 'bg-tg-button' : 'bg-brand-muted'
  }`} />
);
const Line = () => <span className="flex-1 h-px bg-brand-border" />;

// ── panel scaffold ──────────────────────────────────────────────────────
function Panel({
  n, title, done, sub, children,
}: { n: number; title: string; done?: boolean; sub?: React.ReactNode; children?: React.ReactNode }) {
  // A DONE STAGE IS ONE LINE (21 Oct 2026 audit: three full cards, each a title,
  // a "✓ done" and a sentence, said every figure of a settled deal a second
  // time, above the summary card). Title, where and when, then the tick —
  // wrapping onto a second line only on a phone. A done stage with a body
  // (none has one today) keeps the full card.
  if (done && Children.toArray(children).length === 0) {
    return (
      <section className="card-tight flex items-baseline justify-between gap-2">
        {/* The title stays a heading (h3, as in the full card) for the stage list. */}
        <div className="text-body min-w-0">
          <h3 className="inline text-body font-medium"><span className="text-tg-hint mr-1.5">{n}</span>{title}</h3>
          {sub && <span className="text-meta text-tg-hint"> · {sub}</span>}
        </div>
        <span className="text-pos text-meta shrink-0">✓ done</span>
      </section>
    );
  }
  return (
    <section className="card">
      <header className="flex items-baseline justify-between gap-2 mb-1.5">
        <h3 className="text-body font-medium">
          <span className="text-tg-hint mr-1.5">{n}</span>{title}
        </h3>
        {done && <span className="text-pos text-meta">✓ done</span>}
      </header>
      {sub && <p className="text-meta text-tg-hint mb-2">{sub}</p>}
      {children}
    </section>
  );
}

// ── Live boxes (operator, 1 Sept: "why is WK's recorded payment not
// visible here?") — every receipt lands on the party's BOX, whether or not it
// came through this card's stages. The box is the truth; the card now shows
// it — in BoxLines' words, no colour (§5.4): "customer owes you 2,56,183 AED
// · seller square".
/** WHAT THE CUSTOMER STILL HAS TO PAY (operator, 17 Sept 2026: "for record
 *  money in after this cheque screen, how am I supposed to calculate the
 *  remaining amount?"). Face plus the customer's fee, less what the desk
 *  already holds of theirs in the cheque's asset — the deposit and every
 *  tranche taken in. One line, and the "Take customer money in" door carries
 *  the figure into Record money's amount box, still editable there. On a
 *  cheque whose asset is a coin the remainder depends on the rate typed at
 *  handover, so the line names the AED and the door carries no amount.
 *
 *  The customer's asset is the CHEQUE'S asset — the deposit's — so a tranche
 *  lands where the delivery step reads it as prepaid (DeliverInline counts the
 *  box in custAsset only). Forcing AED here sent WK May's USDT tranches to a
 *  box the cheque never looked at (17 Sept 2026). */
/** The figure CustomerStillOwes draws, as a figure — one copy, read by the rebuilt Cheque progress too (screens/v2/ChequeProgress).
 *  `held`: what the desk holds of the customer's in the cheque's asset (negative: their box owes); `still`: what is left to
 *  receive, AED cheques only. Both null while the box is not READ — as 0 it made "still to receive" the whole cheque (Astra,
 *  22 Oct). The header's boxes line (BoxesNow) says why — failed or still waiting; this figure simply does not claim. */
export function stillToReceive(box: Box, custAsset: string, owedAed: number): { held: number | null; still: number | null } {
  const held = box.read ? -partyBalanceOf(box.balances, custAsset) : null;   // sign law: negative = the desk holds theirs
  const still = custAsset === 'AED' && held !== null ? Math.max(0, Math.round((owedAed - held) * 100) / 100) : null;
  return { held, still };
}

export function CustomerStillOwes({ customerSlug, custAsset, owedAed, href }: {
  customerSlug: string; custAsset: string; owedAed: number; href: string;
}) {
  const box = useBoxes([customerSlug]).boxes[0] ?? NO_BOX;
  const { held, still } = stillToReceive(box, custAsset, owedAed);
  const isAed = custAsset === 'AED';
  // 22 Oct 2026 sweep: the figure he acts on leads ("still to receive"), the cheque and what the desk holds follow, and
  // only the figure is in the figure's face (the whole sentence was mono). "Cheque", not "owes": the box line above already
  // says what the customer owes NOW, and two "owes" with two figures read as one disagreeing with the other. At lg the door
  // is the sentence's own size and never breaks mid-link (it was 14px beside 12px words and wrapped after "customer").
  return (
    <p className="text-meta">
      {isAed && held !== null && (
        <span className="text-tg-hint">
          Still to receive <span className="amt-key text-tg-text">{fmtAmount(still ?? 0, 'AED')}</span> AED · cheque + fee {fmtAmount(owedAed, 'AED')} · holds {fmtAmount(held, 'AED')}
          <span className="max-lg:hidden">{' · '}</span>
        </span>
      )}
      {!isAed && (
        <span className="text-tg-hint">Cheque + fee {fmtAmount(owedAed, 'AED')} AED · balance in {displayAsset(custAsset)} follows the handover rate<span className="max-lg:hidden">{' · '}</span></span>
      )}
      {/* Below lg the door stands on its own line, a tap high — inline at the
          end of a three-line paragraph it was a 12px target (19 Sept 2026). */}
      <Link className="btn-link max-lg:block max-lg:mt-1 max-lg:py-1 lg:text-meta lg:whitespace-nowrap" to={href + (still && still > 0 ? `&amount=${encodeURIComponent(String(still))}` : '')}>
        Take customer money in →
      </Link>
    </p>
  );
}

/** ONE PARTY'S BOX, AS THE PAGE MAY USE IT. `read` is the ONE rule for "may a figure, a "square" or a suggestion be drawn from this
 *  box": the box was READ — the party's OWN row of a roster that succeeded whole, or the party's own read. Three ways it was not:
 *    - `failed`: the roster failed (isError — a refresh that failed over cached rows included: the rows are old, and nothing is
 *      claimed from them), or answered HTTP 200 with `balances_read: false` — the books' own word that a ledger list could not be
 *      read, every row of it then carrying `balances: {}`, the payload of a party who owes nothing (Astra, 22 Oct 2026: "customer
 *      square", and an unread prepaid balance counted as 0) — or the party's own read failed.
 *    - `waiting`: nothing has come back yet — and a first read opened OFFLINE is paused, with no data and no fetch in flight, so
 *      it never comes (Appendix B). Waiting is not empty either.
 *    - the party is NOT IN THE ROSTER. The roster is the first 200 active parties alphabetically (pageSize 200 — the endpoint's
 *      cap is 500), so a customer past it has no row, and "no row" read as `?? 0` was a customer who had paid nothing: the full
 *      remainder suggested, "still owed" printed, no word (Astra, 23 Oct 2026). That party is asked for by name (`['party', slug]`,
 *      the party page's own key) and is `waiting`, then `read` or `failed` like any other — the same answer in the same shape.
 *  The header's line is the one message about it ("one failure, one message"); the other places draw nothing of their own. */
export type Box = {
  read: boolean;
  failed: boolean;
  waiting: boolean;
  /** The party's balances, signed, only when `read`; {} otherwise (and `{}` read is "nothing held" — only a read box says so). */
  balances: Record<string, number>;
  /** What the Details of the line say when `failed`: the books' sentence for an error, ours for a 200 that said it could not read. */
  why: unknown;
};
export const NO_BOX: Box = { read: false, failed: false, waiting: false, balances: {}, why: null };

export function useBoxes(slugs: Array<string | undefined>) {
  const roster = useQuery({
    queryKey: ['parties', 'balances'],
    queryFn: () => api.listParties({ with_balances: true, pageSize: 200 }),
  });
  const rosterFailed = roster.isError || balancesUnread(roster.data);
  const rosterRead = roster.isSuccess && !rosterFailed;
  // A party's row in a roster that was read whole — `balances` is absent only on a payload that did not ask for them.
  const rows = slugs.map((slug) => (rosterRead && slug ? roster.data?.parties.find((p) => p.slug === slug)?.balances : undefined));
  const own = useQueries({
    queries: slugs.map((slug, i) => ({
      queryKey: ['party', slug ?? '', 'dubaiotc'],
      queryFn: () => api.partyDetail(slug ?? '', 'dubaiotc'),
      enabled: !!slug && rosterRead && rows[i] === undefined,
    })),
  });
  const boxes = slugs.map((slug, i): Box => {
    if (!slug) return NO_BOX;
    if (rosterFailed) return { ...NO_BOX, failed: true, why: roster.isError ? roster.error : BALANCES_UNREAD };
    if (!rosterRead) return { ...NO_BOX, waiting: true };
    const row = rows[i];
    if (row !== undefined) return { ...NO_BOX, read: true, balances: row };
    const mine = own[i];
    if (mine?.isError) return { ...NO_BOX, failed: true, why: mine.error };
    if (mine?.isSuccess) return { ...NO_BOX, read: true, balances: mine.data.balances };
    return { ...NO_BOX, waiting: true };
  });
  const failed = boxes.find((b) => b.failed);
  return {
    boxes,
    failed: !!failed,
    waiting: !failed && boxes.some((b) => b.waiting),
    why: failed?.why,
    /** Try again: the roster when it failed, else each party whose own read failed. */
    retry: () => {
      if (rosterFailed) void roster.refetch();
      else own.forEach((q, i) => { if (boxes[i]?.failed) void q.refetch(); });
    },
  };
}

export function BoxesNow({ customerSlug, sellerSlug }: { customerSlug?: string; sellerSlug?: string }) {
  const { boxes: [customer = NO_BOX, seller = NO_BOX], failed, waiting, why, retry } = useBoxes([customerSlug, sellerSlug]);
  // A party that holds several assets showed them all on one line, and two of those lines wrapped into each other. Two boxes
  // each, the rest one tap away (22 Oct 2026 sweep) — nothing is dropped: "+1 more" opens the line whole.
  const [all, setAll] = useState(false);
  const cell = (label: string, box: Box) => {
    if (!box.read) return null;
    const parts = Object.entries(box.balances)
      .map(([a, v]) => { const w = boxWords(Number(v), a); return w === 'square' ? '' : `${w} ${displayAsset(a)}`; })
      .filter(Boolean);
    return { label, parts };
  };
  // THE BOXES THAT COULD NOT BE READ ARE SAID, NOT LEFT OUT (22 Oct 2026 sweep): with this line simply gone the page read as
  // a deal whose parties owe nothing. It is also the read stage 3's prepaid figure comes from — see DeliverInline.
  if (failed) return <ReadFailed what="the boxes" error={why} onRetry={retry} />;
  // WAITING is a line, not nothing: a first read opened offline never answers, and a header with no boxes line reads as a deal
  // whose parties owe nothing. A party past the roster's cap is waited for by name, the same way.
  if (waiting) return <Pending what="Reading the boxes…" inline />;
  // One line per party: joined with " · " a customer holding three assets wrapped into the seller's —
  // "… owes you 987,654,321.50 INR · seller you hold …" — and the eye could not tell whose figure it was reading.
  const cells = [cell('customer', customer), cell('seller', seller)].filter((c): c is { label: string; parts: string[] } => c !== null);
  if (cells.length === 0) return null;
  const SHOWN = 2;
  const hidden = all ? 0 : cells.reduce((n, c) => n + Math.max(0, c.parts.length - SHOWN), 0);
  return (
    <p className="text-meta text-tg-hint">
      {cells.map((c) => {
        const parts = all ? c.parts : c.parts.slice(0, SHOWN);
        return <span key={c.label} className="block">{c.label} {parts.length ? parts.join(' · ') : 'square'}</span>;
      })}
      {hidden > 0 && <button type="button" className={TAP} onClick={() => setAll(true)}>+{hidden} more</button>}
    </p>
  );
}

/** What a stage says while it waits for the fresh read: the wait, or — when the read failed — the failure and the way to ask again. Nothing can be paid from it. */
function Reconciling({ error, onRetry }: { error: unknown | null; onRetry: () => void }) {
  return error ? <ReadFailed what="this cheque" error={error} onRetry={onRetry} /> : <Pending what="Reading the cheque…" />;
}

// ── Stage 2 inline form — MULTI-LEG (operator, 1 Sept) ──────────────────
// A big cheque's seller rarely takes one payment in one asset: GRYPTO's took
// 10,50,000 AED cash + the rest in USDT @ 3.676. Each leg posts as it happens;
// coins carry their agreed rate (the AED meter fills at that rate and the
// conversion parks on clearing); the operator declares "fully paid" when the
// meter says so. Delivery later reads the seller's true cut from the meter.
export function PaySellerInline({ dealId, face, defaultFeeBps, paidSoFar, sellerSlug, uncounted, readTick, reading, readError, onRetry, onAsk, onDone }: {
  dealId: string; face: number; defaultFeeBps: number; paidSoFar: number;
  /** The cheque's seller — the party this leg pays, so the pay-out picker
   *  leads with the rails he is usually paid on (12 Sept 2026). */
  sellerSlug: string;
  /** Payments the ledger holds that the cheque's meter never counted (the deal read's mc_uncounted_legs): finished first, from the books' own figures. */
  uncounted: McUncountedLeg[];
  /** The deal read, as the stage sees it: the tick it started at, whether one is out, whether the last one failed — and how to ask again: after a press ends the form waits for a read that STARTED after
   *  it (useReconcile). `onRetry` is the operator's "Try again" on a failed read; `onAsk` the stage's own request for the exact cheque. */
  readTick: number; reading: boolean; readError: unknown | null; onRetry: () => void; onAsk: () => void;
  onDone: () => void;
}) {
  const treasury = useQuery({ queryKey: ['treasury', 'balances'], queryFn: () => api.treasuryBalances('dubaiotc') });
  const [rail, setRail]     = useState<RailValue>({ slug: '', asset: '' });
  // Two places, as the booking screen writes a fee ("0.40", not "0.4") — the
  // same number to the bps, so nothing it posts changes (21 Oct 2026 audit).
  const [feePct, setFeePct] = useState((defaultFeeBps / 100).toFixed(2));
  const [armed, setArmed]   = useState(false);   // two-tap confirm for early completion
  const [notSaved, setNotSaved] = useState<string | null>(null);   // a press that could not be written down on this device is not sent (lib/chequePress.ts PressNotSaved)
  const rows = useMemo(() =>
    (treasury.data?.rows ?? []).filter((r) => MC_PAY_OUT_KINDS.includes(r.treasuryKind ?? ''))
  , [treasury.data]);

  const owed      = Math.round(face * (1 + (Number(feePct) || 0) / 100) * 100) / 100;
  const remaining = Math.round((owed - paidSoFar) * 100) / 100;
  const isCoin    = rail.asset !== '' && rail.asset !== 'AED';
  // THE LEG IS ONE THREE-WAY SUM, coins × rate = AED (operator, 12 Sept 2026:
  // "screens everywhere for asset-1, asset-2 & rate and 3-way auto
  // calculation ... even if I directly paste the rates, total should be
  // there"). The shared calculator owns the three boxes: the pair is the
  // rail's asset against AED, and the rate reads the way the desk says it —
  // AED per USDT (3.676, the way GRYPTO's was agreed), INR per AED (22.7) on
  // an INR cash rail — and typing or pasting any two fills the third on every
  // keystroke. With an AED rail (or none picked yet) the pair collapses to the
  // one AED box, exactly as before.
  const payAsset = rail.asset || 'AED';
  const math     = useTradeMath(payAsset, 'AED');
  const amount   = math.amountOf(payAsset);             // what leaves the rail, in the rail's own asset
  const legAed   = Number(math.amountOf('AED')) || 0;   // what the seller's AED meter gains
  // THE ROUTE'S rate_to_aed IS AED PER ONE UNIT OF THE RAIL'S ASSET, whichever
  // way the desk quotes the pair (manager-cheque.ts meters desk_pay_amount ×
  // rate). On an INR rail the calculator's rate is INR per AED, so what is
  // posted is its inverse — sent as written, 22.7 would meter an INR leg
  // ~500× high (12 Sept 2026 review). Kept at the eight places the route keeps
  // a rate, so the figure posted is the figure the ledger carries. The same
  // guard the booking screen calls aedIsBase.
  const rateN     = Number(math.rate) || 0;
  const aedIsBase = foldAsset(math.base) === 'AED';
  const rateToAed = rateN > 0 ? Number(fmtRateExact(aedIsBase ? 1 / rateN : rateN)) : 0;
  // What this leg leaves of the remaining figure, to the fils — and whether
  // that is within dust, which is what closes the stage.
  const legRounded = Math.round(legAed * 100) / 100;
  const leftover   = Math.abs(remaining - legRounded);
  const inFull     = leftover <= dustFor('AED') && legAed > 0;

  // ONE TAP IS ONE PRESS (lib/chequePress.ts, 26 Oct 2026). The key, the amount, the rate, the rail, "this is the last payment" and the index are fixed when
  // the operator taps and HELD until the books answer; an answer ends the press (counted — or counted already — or refused), anything else leaves it held and
  // the one button sends it again AS IT WAS. Nothing is recomputed on a retry: the paid total moves with a refresh, and a retry that followed it was a second payment.
  const held = useHeld<SellerLegBody>('seller-leg', dealId);
  // A press that ENDS — counted here, in another tab, settled by the books' own list, or refused — is followed by a fresh read of the cheque, and the form WAITS for it (useReconcile): the
  // payment's inputs are cleared unless this tab's own refusal left them for correcting. Another tab's answer ends this page's copy of the press without this page hearing.
  // `refused` is set the moment the books refuse THIS tab's press — before the press is ended, so before any render can see its end: the mutation's own error state flips only AFTER its
  // onError returns, and a refusal must leave the typed payment for correcting.
  const refused = useRef(false);
  const reconciling = useReconcile('seller-leg', dealId, { tick: readTick, reading, failed: readError != null }, onAsk, () => { if (!refused.current) math.reset(); refused.current = false; });
  const send = useMutation({
    // The press names its own cheque: the screen may be showing another by the time the books answer (the route's param changed under a reused component).
    mutationFn: (h: Held<SellerLegBody>) => api.payMcSeller(h.dealId, h.body),
    onSuccess: (_r, h) => { presses.answered('seller-leg', h.dealId, 'done', h.body); tg()?.HapticFeedback.notificationOccurred('success'); if (h.dealId === dealId) math.reset(); onDone(); },
    onError: (e, h) => {
      const how = answerOf(e);
      if (how === 'refused') refused.current = true;
      presses.answered('seller-leg', h.dealId, how, h.body);
      tg()?.HapticFeedback.notificationOccurred('error');
      // The books looked and refused: read the cheque again, so what they now say — a payment they hold uncounted, a total that moved, a cheque that has moved on — is what the screen shows.
      if (how === 'refused') onDone();
    },
  });
  const post = () => {
    setArmed(false);
    const h = tryBegin('seller-leg', dealId, sellerLegBody({
      rail: rail.slug, asset: rail.asset, amount: Number(amount), isCoin, rateToAed, remaining, legAed, paidSoFar, dust: dustFor('AED'),
    }, mintLegKey()), setNotSaved);
    if (h) send.mutate(h);
  };
  const sendAgain = () => {
    const h = presses.resend<SellerLegBody>('seller-leg', dealId);
    if (h) send.mutate(h);
  };
  // A payment the ledger holds and the meter never counted is FINISHED by sending it again as it was — its own key, its own figures, taken from the books' list, never from this
  // form — and it goes through the SAME held press as any payment: a Finish whose answer is lost is held and sent again, and a Finish that counted while its answer was lost
  // does not leave the form drawn with nothing to say so.
  const finishNow = (u: McUncountedLeg) => {
    const body = finishLegBody(u);
    if (!body) return;
    const h = tryBegin('seller-leg', dealId, body, setNotSaved);
    if (h) send.mutate(h);
  };
  const complete = useMutation({
    // completion moves no money — no rail rides along (1 Sept: the selected
    // coin rail + asset AED tripped the bucket guard)
    mutationFn: () => api.payMcSeller(dealId, {
      desk_treasury_slug: '', desk_pay_asset: 'AED', desk_pay_amount: 0,
      partial: true, complete: true,
    }),
    onSuccess: () => { tg()?.HapticFeedback.notificationOccurred('success'); onDone(); },
    onError:   (e) => { tg()?.HapticFeedback.notificationOccurred('error'); if (answerOf(e) === 'refused') onDone(); },
  });

  // THE TOTAL DECIDES THE QUANTITY, not the other way round. Typing a coin
  // amount by hand and hoping it meters to the owed figure is where the
  // rounding slivers came from; this fills the coin amount from the
  // remaining total at the rate, rounded UP at the coin's own precision so
  // the meter reaches the figure.
  const payRemaining = () => {
    if (!isCoin) { math.preset({ asset: 'AED' }, String(remaining)); return; }
    // Divided by the rate the ROUTE will meter at, at the asset's own places
    // (two on INR cash, six on a coin), so the meter reaches the figure
    // whichever way the pair is quoted. Declared afresh — the rate first,
    // then the coins — so the AED box derives from the two. Left as it stood,
    // a total typed earlier would anchor and the RATE would move, not the
    // coins.
    const unit  = isFiat(foldAsset(rail.asset)) ? 100 : 1e6;
    const coins = String(Math.ceil((remaining / rateToAed) * unit) / unit);
    math.reset();
    math.preset('rate', math.rate);
    math.preset({ asset: rail.asset }, coins);
  };

  const accountOf = (slug: string | null | undefined, asset?: string | null) =>
    railName((slug ? rows.find((r) => r.slug === slug && (!asset || sameAsset(r.asset, asset))) : undefined) ?? { slug: slug ?? '' });

  // A PRESS THE BOOKS HAVE NOT ANSWERED IS SHOWN, NOT THE FORM: it is sent again as it was, or it is not sent. (While it is out, the form is not there to be changed.)
  if (held) {
    const b = held.body;
    const sending = held.state === 'sending';
    return (
      <div className="space-y-2">
        <StateLine
          tone={sending ? 'neutral' : 'warn'}
          more={sending ? undefined : 'The books did not answer this payment, so it may or may not be counted. It carries its own key and its figures are fixed: sending it again counts it once, and if the books already hold it they say so and post nothing more. It stays until the books have answered it.'}
        >
          {sending ? 'Posting this payment…' : 'No answer — send it again; it counts once.'}
        </StateLine>
        <p className="text-meta text-tg-hint">
          {fmtAmount(b.desk_pay_amount, b.desk_pay_asset)} {displayAsset(b.desk_pay_asset)} from {accountOf(b.desk_treasury_slug, b.desk_pay_asset)}
          {b.rate_to_aed ? ` at ${fmtRateExact(b.rate_to_aed)} AED each` : ''}{b.complete ? ' · the last payment' : ''}
        </p>
        <button className="btn-primary w-full text-body py-2.5 lg:py-1.5" disabled={sending} onClick={sendAgain}>
          {sending ? 'Posting…' : 'Send again'}
        </button>
      </div>
    );
  }
  // The press has ended and the cheque has not been read again: nothing can be paid from what is on screen.
  if (reconciling) return <Reconciling error={readError} onRetry={onRetry} />;
  // A PAYMENT THE LEDGER HOLDS AND THE METER NEVER COUNTED comes first: no other payment and no "fully paid" is taken until it is sent again (the route
  // refuses them: leg_uncounted). It is finished from the books' own figures. One gold button — the oldest.
  if (uncounted.length > 0) {
    return (
      <div className="space-y-3">
        {uncounted.map((u, i) => (
          <div key={u.reference} className="space-y-1.5">
            <StateLine
              tone="warn"
              more="This payment is in the books but not counted on this cheque. Finishing it sends it again as it was — its own key, its own figures — so it is counted once."
            >
              {/* An entry posted before 26 Oct may record no asset: its figure is said as it is and the asset as not recorded — never made AED. */}
              Not counted: {u.desk_pay_asset ? <>{fmtAmount(u.desk_pay_amount, u.desk_pay_asset)} {displayAsset(u.desk_pay_asset)}</> : <>{fmtAmount(u.desk_pay_amount, undefined, 8)} · asset not recorded</>}
              {u.desk_treasury_slug ? ` from ${accountOf(u.desk_treasury_slug, u.desk_pay_asset)}` : ''}.
            </StateLine>
            {finishLegBody(u)
              ? (
                <button className={`${i === 0 ? 'btn-primary' : 'btn-secondary'} w-full text-body py-2.5 lg:py-1.5`} onClick={() => finishNow(u)}>
                  Finish this payment
                </button>
              )
              : <p className="text-meta text-tg-hint">Not enough was recorded to send it again here.</p>}
          </div>
        ))}
        {notSaved && <StateLine tone="bad" more={NOT_SAVED_MORE}>{notSaved}</StateLine>}
        {send.isError && <ServerLine error={send.error} />}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FeePctField label="Seller fee %" value={feePct} onChange={setFeePct} className={FEE_FIELD_WIDTH} />
      {/* A NEGATIVE REMAINDER IS AN OVERPAYMENT, NOT AN UNPAID BALANCE. The
          meter can pass face + fee — a seller paid above the fee keeps the
          difference, and delivery reads exactly that as their cut. Every
          sentence here used to describe the gap as money still owed, so an
          overpaid seller read as "−N AED stays unpaid forever". */}
      <p className="text-meta text-tg-hint">
        owed {fmtAmount(owed, 'AED')} (face + {feePct || '0'}%) · paid {fmtAmount(paidSoFar, 'AED')} ·{' '}
        {remaining >= 0 ? `remaining ${fmtAmount(remaining, 'AED')}` : `over by ${fmtAmount(-remaining, 'AED')}`}
      </p>
      <RailPicker
        value={rail} onChange={setRail} rows={rows}
        allowedKinds={MC_PAY_OUT_KINDS}
        partySlug={sellerSlug}
        loading={treasury.isFetching || treasury.fetchStatus === 'paused'}
        readFailed={treasury.isError}
        placeholder="Pay-out account"
      />
      {/* A READ THAT FAILED IS NOT AN EMPTY LIST (22 Oct 2026 sweep): opened, the picker would say "No such account". */}
      {treasury.isError && <ReadFailed what="the accounts" error={treasury.error} onRetry={() => void treasury.refetch()} />}
      {/* THE BOX THAT MOVES SAYS SO (12 Sept 2026): the one the calculator
          fills carries "auto" as its label hint and muted figures, so the
          operator sees at a glance which of the three will change when they
          type in another. The calculator decides (math.isDerived /
          math.rateIsDerived) — this screen never works it out itself. The box
          stays editable: typing in it makes it a fact and moves the "auto"
          elsewhere. The label is always drawn (§5.4), so the row does not
          jump when the rail changes. */}
      <Field label="Amount" hint={math.isDerived(payAsset) ? 'auto' : undefined}>
        <div className="flex gap-2">
          <input className={`input flex-1${math.isDerived(payAsset) ? ' text-tg-hint' : ''}`} value={amount} onChange={(e) => math.setAmount(payAsset, e.target.value)} inputMode="decimal" aria-label="Leg amount" />
          <AssetBox asset={rail.asset} />
        </div>
        {/* "Pay the remaining" as a link under the row (§5.4) — it used to
            squeeze the input to ~120px. */}
        {remaining > 0 && (!isCoin || rateToAed > 0) && (
          <button type="button" className="btn-link text-body lg:text-meta mt-1 max-lg:py-1" onClick={payRemaining}>
            Pay the remaining
          </button>
        )}
      </Field>
      {isCoin && (
        <>
          <Field label="Rate" hint={math.rateIsDerived ? `${math.rateWords} · auto` : math.rateWords}>
            <input className={`input w-full${math.rateIsDerived ? ' text-tg-hint' : ''}`} value={math.rate} onChange={(e) => math.setRate(e.target.value)} inputMode="decimal" aria-label={`Rate, ${math.rateWords}`} />
          </Field>
          <Field label="Total in AED" hint={math.isDerived('AED') ? 'auto' : undefined}>
            <div className="flex gap-2">
              <input className={`input flex-1${math.isDerived('AED') ? ' text-tg-hint' : ''}`} value={math.amountOf('AED')} onChange={(e) => math.setAmount('AED', e.target.value)} inputMode="decimal" aria-label="Leg total in AED" />
              <AssetBox asset="AED" />
            </div>
          </Field>
        </>
      )}
      {/* The arithmetic line, short (§5.4): "= 2,55,166 AED · pays the seller
          in full (1 AED rounding)". 21 Oct 2026 audit: "= 36,760 AED at 3.676
          AED per USDT·TRC" said the Total box and the Rate box a second time,
          so the line is left for the one thing they do not say — that this leg
          pays the seller in full. */}
      {isCoin && Number(amount) > 0 && rateToAed > 0 && inFull && (
        <p className="text-meta text-tg-hint">
          Pays the seller in full{leftover > 0 ? ` (${fmtAmount(leftover, 'AED')} AED rounding)` : ''}
        </p>
      )}
      {/* ONE primary action (operator, 1 Sept: "WHY 2 OPTIONS?") — two equal
          buttons made the wrong tap easy. Posting a leg is THE action; the
          completion appears as its own full button only once the meter is
          settled, and an early completion is a deliberate two-tap override. */}
      {/* ONE GOLD BUTTON (emphasis rule): once the meter is settled the
          completion below is the action, and this one steps back. */}
      <button
        className={`${Math.abs(remaining) <= 1 && paidSoFar > 0 ? 'btn-secondary' : 'btn-primary'} w-full text-body py-2.5 lg:py-1.5`}
        disabled={!rail.slug || !(Number(amount) > 0) || (isCoin && !(rateToAed > 0))}
        onClick={post}
      >
        Post this leg
      </button>
      {Math.abs(remaining) <= 1 && paidSoFar > 0 ? (
        <button
          className="btn-primary w-full text-body py-2.5 lg:py-1.5"
          disabled={complete.isPending}
          onClick={() => complete.mutate()}
        >
          {complete.isPending ? 'Posting…' : 'Seller fully paid ✓ — unlock delivery'}
        </button>
      ) : paidSoFar > 0 ? (
        <button
          className={`btn-link w-full text-body lg:text-meta max-lg:py-2 ${armed ? 'text-neg' : 'text-tg-hint'}`}
          disabled={complete.isPending}
          onClick={() => (armed ? complete.mutate() : setArmed(true))}
        >
          {complete.isPending ? 'Posting…'
            : armed
              ? (remaining > 0
                  ? `Tap again — ${fmtAmount(remaining, 'AED')} AED stays unpaid`
                  : `Tap again — the seller keeps ${fmtAmount(-remaining, 'AED')} AED over face + fee`)
              : (remaining > 0
                  ? `settled short? mark fully paid at ${fmtAmount(paidSoFar, 'AED')}`
                  : `paid over? mark fully paid at ${fmtAmount(paidSoFar, 'AED')}`)}
        </button>
      ) : null}
      {notSaved && <StateLine tone="bad" more={NOT_SAVED_MORE}>{notSaved}</StateLine>}
      {(send.isError || complete.isError) && <ServerLine error={send.error ?? complete.error} />}
    </div>
  );
}

// ── Stage 3 inline form ─────────────────────────────────────────────────
/** A CLOSURE claim — "closes to zero" — is made at the LEDGER'S OWN precision, per asset (lib/ledger-scale.ts: the BFF's ASSET_SCALE, the one
 *  copy the console has): a currency to the fil, a stablecoin to six places, SOL and GRAM to nine. The box words' dust (up to 1 AED, 0.5 USDT)
 *  is for reading a list; money that is not zero is not settled, whatever a list rounds it to (Astra, round 2e: 0.01 AED still owed was said to
 *  close, and an existing 1 AED or 0.4 USDT vanished from the projection). And a balance is PRINTED at that precision too — never a cap of the
 *  screen's own (round 2f: fmtQuantity stops at eight, so 0.000000001 SOL read "≈0" and 0.000000006 SOL read 0.00000001): a figure the books
 *  hold open is shown to the place they hold it, never "≈0", never a zero it is not. */
const ledgerFigure = (v: number, asset: string): string => fmtAmount(v, asset, ledgerScale(asset));

/** THE AFTER-DELIVERY LINE IS ONE ROW (a money guard is one short line), and nothing on it is ever cut: which way the first balance runs,
 *  its FULL figure and its unit, and the count of the others. At 360 the shell's gutters (p-3) and the form card's padding leave the state
 *  line 310px; ROW_PX keeps a few px of air under that, for the width guess's own error (lib/glyph-width.ts). What gives way, in this order,
 *  when the sentence does not fit: the count's words ("· +1 more" becomes "· +1"), then the sentence's size (the whole line takes the meta
 *  role — the Details link beside it is meta already) with the longer count first. Astra, round 2f: "After: you hold 99,999,999.999999
 *  USDT · +1 more." is 338px; round 2g: shortening only the count left "After: owes you 1,999,999.999999999 GRAM · +1." at 326px. */
export const ROW_PX = 304;
export function fitAfterLine(way: string, sum: string, more: number): { count: string | null; small: boolean } {
  const head = `After: ${way} ${sum}`;
  const width = (count: string | null, small: boolean): number => small
    ? textPx(`${head}${count ? ` ${count}` : ''}.`, 'meta')
    : textPx(head) + (count ? textPx(' ') + textPx(count, 'meta') : 0) + textPx('.');
  if (more < 1) return { count: null, small: width(null, false) > ROW_PX };
  const full = `· +${more} more`, short = `· +${more}`;
  for (const [count, small] of [[full, false], [short, false], [full, true]] as const) {
    if (width(count, small) <= ROW_PX) return { count, small };
  }
  return { count: short, small: true };
}

export function DeliverInline({ dealId, face, defaultFeeBps, deposit, custAsset, customerSlug, started, readTick, reading, readError, onRetry, onAsk, onDone }: {
  dealId: string; face: number; defaultFeeBps: number; deposit: number; custAsset: string; customerSlug: string;
  /** What a started delivery put in the ledger (the deal read's mc_delivery_started), or null: it is finished with ITS figures, not the form's. */
  started: McDeliveryStarted | null;
  /** The deal read, as the stage sees it (see PaySellerInline): after a press ends the stage waits for a read that STARTED after it (useReconcile). */
  readTick: number; reading: boolean; readError: unknown | null; onRetry: () => void; onAsk: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  // WHAT THE CUSTOMER HAS ALREADY PAID ON ACCOUNT, beyond the deposit. 10 Sept
  // 2026: WK May's balance was recorded as a receipt two minutes before
  // delivery, and this form still suggested the full balance — posted twice.
  // The box is the truth (BoxesNow reads the same query): money the desk
  // holds for them past the deposit comes off the suggested balance, and the
  // BFF refuses a balance that money already covers.
  // AN UNREAD BOX IS NOT A BOX AT NOUGHT (Astra, 22 Oct 2026): a failed read, or a 200 with `balances_read: false` (every row then
  // carries `balances: {}`), made `prepaid` 0 — and the balance box was prefilled with the WHOLE remainder, "still owed" printed
  // it, and nothing said the figure came from nothing. Unread, there is no prepaid figure, so nothing is suggested and nothing
  // is said to be owed: the boxes stay blank, the operator types what the customer pays or reads the box again.
  // THE CUSTOMER'S OWN BOX — their row of the roster, or, when the roster does not hold them (it stops at 200 parties), their own
  // read. A customer with no row is not a customer who has paid nothing (Astra, 23 Oct 2026).
  const box = useBoxes([customerSlug]).boxes[0] ?? NO_BOX;
  const boxesRead = box.read, boxesFailed = box.failed;
  const heldTotal = boxesRead ? -partyBalanceOf(box.balances, custAsset) : 0;
  // Same asset as the deposit rail. Not read it is NO figure at all — not the rows a failed refresh left cached, which would print
  // "Already paid … taken off the balance" under a balance box that took nothing off.
  const prepaid = boxesRead ? Math.max(0, heldTotal - deposit) : 0;
  // The fee is negotiable to the end, and an early-paid cheque owes NOTHING at
  // handover (operator, 1 Sept: WK May paid in full before delivery — the old
  // form refused a zero balance and pre-filled a figure that would have
  // charged her twice). Fee % drives the shown total; the balance box is what
  // is ACTUALLY received at handover — zero is a legal, honest answer.
  // Two places, as the booking screen writes a fee ("0.80", not "0.8") — the
  // same number to the bps (21 Oct 2026 audit).
  const [feePct, setFeePct] = useState((defaultFeeBps / 100).toFixed(2));
  // A FEE IS WHOLE BASIS POINTS. The request carries Math.round(feePct × 100)
  // and the route charges face × those bps, so a fee typed finer than a
  // hundredth of a percent was shown here at one rate and charged there at
  // another: 0.655% on a 1,866,365 face is 93.32 AED apart, and the difference
  // stayed on the customer's box after a delivery this screen called square.
  // Derive every figure from the bps that will actually be sent.
  const feeBps = Math.round((Number(feePct) || 0) * 100);
  const feeAmt = aedHalfEven(face * feeBps / 10000);
  // COIN ON THE CUSTOMER SIDE (operator, 2 Sept: "where is rate window for
  // usdt?"). The cheque debt is AED; GRYPTO's deposit is USDT. One agreed rate
  // values the deposit and the balance. The three boxes — coins, rate, AED —
  // belong to the shared calculator (operator, 12 Sept 2026: "3-way auto
  // calculation, that too dynamic which changes with each number I change"),
  // and the rate reads the way the desk says it: AED per USDT, INR per AED on
  // an angadiya deposit. Any of the three can be typed; once the operator
  // types an amount, their figure stands. With an AED customer the pair
  // collapses to the one balance box, as before.
  const coin  = custAsset !== 'AED';
  const math  = useTradeMath(custAsset, 'AED');
  const rateN = Number(math.rate) || 0;
  const amount = math.amountOf(custAsset);   // the balance handed over, in the customer's own asset
  // THE ROUTE'S rate_to_aed IS AED PER ONE UNIT OF THE CUSTOMER'S ASSET, and it
  // values the coin legs at that figure rounded to eight places
  // (manager-cheque.ts: Math.round(rateToAed × 1e8)). On an INR deposit the
  // calculator's rate is INR per AED, so the posted rate is its inverse — sent
  // as written, 22.7 valued the deposit ~500× high, "still owed" collapsed to
  // zero and the cheque discharged against it (12 Sept 2026 review). Every AED
  // figure below is worked at exactly the rate the route will use. The same
  // guard the booking screen calls aedIsBase.
  const aedIsBase = foldAsset(math.base) === 'AED';
  const rateToAed = coin && rateN > 0 ? Number(fmtRateExact(aedIsBase ? 1 / rateN : rateN)) : 0;

  const depositAed = coin ? aedHalfEven(deposit * rateToAed) : deposit;
  const owedAed  = aedHalfEven(face + feeAmt);
  const prepaidAed = coin ? aedHalfEven(prepaid * rateToAed) : prepaid;
  const stillAed = Math.max(0, aedHalfEven(owedAed - depositAed - prepaidAed));
  // THE ROUTE VALUES THE DEPOSIT AND THE BALANCE AS ONE COIN LEG, rounded once.
  // So the balance that closes the customer's box is what the whole coin
  // position must come to, less what already arrived — at the coin's own
  // precision, not at two decimals. Suggesting 501,498.00 USDT where
  // 501,497.997824 was owed valued the pair a fils high and left GRYPTO's
  // customer holding 0.01 AED after a delivery this screen called square
  // (3 Sept audit). Deriving the coins from the AED still owed, itself rounded
  // to the fils, is the same fault by another road: whenever deposit × rate
  // lands on half a fils (12,345 USDT at 3.675) the two roundings differ by a
  // fils (12 Sept 2026 review). So the COIN figure is the first fact on the
  // screen and the AED box is what those coins are worth. The calculator's six
  // places (two on INR) are finer than a fils on every stablecoin the desk
  // takes; on a coin the ledger carries deeper (BTC, SOL) a last-place unit
  // can still be worth more than half a fils, and whatever it leaves behind
  // is stated in the closing line below rather than rounded out of sight.
  const suggested = !boxesRead ? ''
    : coin
      ? (rateToAed > 0 ? fmtQty(Math.max(0, owedAed / rateToAed - deposit - prepaid), custAsset) : '')
      : String(stillAed);
  const [touched, setTouched] = useState(false);
  // Which box has the cursor — its raw string shows while it does (shownAmount): a box the
  // calculator filled reads grouped in thousands, "2,343,299.69", not "2343299.69" (Astra, 21 Oct).
  const [editing, setEditing] = useState<'amount' | 'aed' | null>(null);
  // THE SUGGESTED BALANCE IS THE FIRST FACT, restated whenever it moves — the
  // fee, the rate valuing the deposit, a receipt landing on the box — until
  // the operator types an amount. Typing the rate is not that: the rate
  // re-values the deposit, which is exactly what moves the figure. Declared
  // from a clean slate each time — the coins, then the rate, so the AED box
  // is the one that derives — and no earlier ownership can make the rate move
  // instead of the coins. Once the operator has typed, the calculator's own
  // rules hold: a corrected coin figure re-values the AED at the agreed rate;
  // a corrected AED total keeps the coins and re-derives the rate (11 Sept).
  // A layout effect, so the figures are in the boxes before the paint, not
  // one frame after it. A zero balance (paid in full before handover — WK
  // May, 1 Sept) is stated in both boxes outright: the calculator derives
  // nothing from a zero. No rate yet: both boxes stay blank and say so.
  // The reset stays (12 Sept 2026, after the hook's preset learned to anchor
  // on its own): it is what blanks both boxes again when the operator clears
  // the rate before typing an amount, and a plain preset would leave the old
  // coin figure standing beside an empty rate.
  useLayoutEffect(() => {
    if (touched) return;
    math.reset();
    // BOXES NOT READ (failed, or still waiting): nothing to prefill from. Without this the empty suggestion ('') would read as a ZERO
    // below — "paid in full before handover" — and write 0 into the AED box. The boxes stay blank; a rate the operator typed is put back.
    if (!boxesRead) { if (coin && rateToAed > 0) math.preset('rate', math.rate); return; }
    if (!coin) { math.preset({ asset: 'AED' }, suggested); return; }
    if (!(rateToAed > 0)) return;
    // THE ZERO IN AED GOES FIRST (12 Sept 2026). The calculator holds two
    // facts and calls the third "auto" — and now shows it. Declared last, the
    // zero pushed the RATE out: the box the operator had just typed read
    // "auto", and a coin figure typed afterwards left the AED box at 0. First,
    // it is the AED that the coins and the rate push out — the same box that
    // is auto whenever the balance is not zero — and it keeps showing 0.
    if (Number(suggested) === 0) math.preset({ asset: 'AED' }, '0');
    math.preset({ asset: custAsset }, suggested);
    math.preset('rate', math.rate);
  }, [suggested, math.rate, touched, boxesRead]);   // eslint-disable-line react-hooks/exhaustive-deps
  // What the route will actually discharge, valued its way, and what that
  // leaves on the customer's box. Positive means the customer still owes the
  // desk; negative means the desk is holding money of theirs.
  // A PROJECTION NEEDS AN AMOUNT: a box that is blank is not a balance of 0 — Number('') is 0, and the whole remainder read as
  // "customer would still owe" beside "not suggested" (Astra, 23 Oct 2026). And the verdict is a claim about the customer's BOX,
  // so it needs the box READ: with the box unread — failed, or still waiting — only the sum of what was typed is said.
  const amountN  = Number(amount);
  const amountOk = amount.trim() !== '' && Number.isFinite(amountN) && amountN >= 0;
  const paidAed   = coin ? aedHalfEven((deposit + (Number(amount) || 0)) * rateToAed) : deposit + (Number(amount) || 0);
  // THE BOX AFTER DELIVERY = the box as READ + this delivery's own legs (bff/src/routes/manager-cheque.ts), and nothing assumed about
  // what the box already held. The legs that touch the customer: the cheque in (+face AED); its fee discharged (+fee AED, a world
  // leg); the balance paid in (−balance, the coin); and ONE coin pair per coin — the DEPOSIT and this BALANCE only (`coinLegs`) —
  // converted back out of clearing at the one rate (+deposit + balance of the coin; −what that is worth in AED). Net, in AED
  // `aedLeft` = owed − worth, in the coin +deposit, and no other asset is touched. What the customer paid on account beyond the
  // deposit (`prepaid`) reaches the duplicate-payment guard, never a posting: it stays in the box, as coins, beside the AED.
  // (Astra, round 2d: "the customer's AED closes to zero" was said over a box that held 500 AED already, and "the box closes" over
  // one that held EUR — a delivery's change is not the box.) A party's stablecoins are ONE line, USDT, in the box as read.
  const aedLeft = aedHalfEven(owedAed - paidAed);
  const coinLine = ['USDT', 'USDC'].includes(foldAsset(custAsset)) ? 'USDT' : foldAsset(custAsset);
  const afterBox: Record<string, number> = { ...box.balances };
  afterBox.AED = (afterBox.AED ?? 0) + aedLeft;
  afterBox[coinLine] = (afterBox[coinLine] ?? 0) + deposit;
  for (const a of Object.keys(afterBox)) afterBox[a] = atLedger(afterBox[a] ?? 0, a);
  // What stays open — AED, then the coin, then whatever else the box held — is every balance that is not ZERO AT THE LEDGER'S
  // PRECISION: 0.01 AED is open, 0.4 USDT is open. "Closes to zero" is the empty list, never a list the box words found small.
  const staysOpen = [...new Set(['AED', coinLine, ...Object.keys(afterBox)])].filter((a) => (afterBox[a] ?? 0) !== 0);
  /** One balance in the header's words: which way it runs, and its figure with its unit. */
  const wording = (a: string) => {
    const v = afterBox[a] ?? 0;
    return { way: v > 0 ? 'owes you' : 'you hold', sum: `${ledgerFigure(Math.abs(v), a)} ${displayAsset(a)}` };
  };
  /** …with the figure and unit one no-break group (a phone breaks BETWEEN words, never inside the sum). */
  const say = (a: string): ReactNode => {
    const { way, sum } = wording(a);
    return <>{way} <span className="whitespace-nowrap">{sum}</span></>;
  };
  const firstOpen = staysOpen[0] === undefined ? null : wording(staysOpen[0]);
  const fit = firstOpen ? fitAfterLine(firstOpen.way, firstOpen.sum, staysOpen.length - 1) : null;
  const afterLine = fit && (
    <>After: {say(staysOpen[0]!)}{fit.count ? <> <span className={fit.small ? 'whitespace-nowrap' : 'whitespace-nowrap text-meta'}>{fit.count}</span></> : null}.</>
  );
  // THE CONFIRM IS A REAL BUTTON (sweep item 17, 14 Sept 2026). The route
  // refuses a balance that money already on the box would cover, and its old
  // sentence ended "pass confirm_money_moved_again:true" — an instruction to
  // a developer, on a screen with no such button. Now the refusal shows the
  // route's own words and one button that resends with that flag, its label
  // carrying the consequence. `again` is the operator's answer, never a
  // default. The flag is not in lib/api.ts's declared body yet (another
  // workstream's file), so the body is built as a value rather than a literal.
  // ONE TAP IS ONE PRESS (lib/chequePress.ts, 26 Oct 2026): the body is fixed at the tap and HELD until the books answer, and sent again as it was. Every
  // delivery leg has a fixed reference, so the same figures sent again finish a delivery whose answer was lost — and other figures are refused (delivery_started),
  // so a retry that recomputed the balance from a box that now holds its own balance leg would have been refused, or worse.
  const held = useHeld<DeliveryBody>('delivery', dealId);
  const reconciling = useReconcile('delivery', dealId, { tick: readTick, reading, failed: readError != null }, onAsk);
  const [discarding, setDiscarding] = useState(false);   // two-tap confirm to discard a held delivery the books never answered
  const [notSaved, setNotSaved] = useState<string | null>(null);
  const submit = useMutation({
    mutationFn: (h: Held<DeliveryBody>) => api.deliverMc(h.dealId, h.body),
    onSuccess: (_r, h) => { presses.answered('delivery', h.dealId, 'done', h.body); tg()?.HapticFeedback.notificationOccurred('success'); onDone(); },
    onError: (e, h) => {
      const how = answerOf(e);
      presses.answered('delivery', h.dealId, how, h.body);
      tg()?.HapticFeedback.notificationOccurred('error');
      // Refused, and not the "already paid on account" panel (which has its own two buttons): read the cheque again — a delivery that has started shows its press.
      if (how === 'refused' && !(e instanceof ApiError && e.code === 'money_already_recorded')) onDone();
    },
  });
  const post = (again?: boolean) => {
    const h = tryBegin('delivery', dealId, deliveryBody({ balance: Number(amount), feeBps, coin, rateToAed, again }), setNotSaved);
    if (h) submit.mutate(h);
  };
  const sendHeldAgain = () => {
    const h = presses.resend<DeliveryBody>('delivery', dealId);
    if (h) submit.mutate(h);
  };
  const startedBody = finishDeliveryBody(started);
  const finishStarted = () => {
    if (!startedBody) return;
    const h = tryBegin('delivery', dealId, startedBody, setNotSaved);
    if (h) submit.mutate(h);
  };
  const alreadyPaid = submit.isError && submit.error instanceof ApiError && submit.error.code === 'money_already_recorded';
  // A 0 that STANDS in the box — typed, or the prefill of a deal paid in full
  // before handover — as against an empty one (21 Oct 2026 audit).
  const typedZero = amount.trim() !== '' && Number(amount) === 0;

  /** The figures of a delivery press, in one short line. */
  const figures = (b: DeliveryBody) =>
    `Balance ${fmtAmount(b.customer_balance_amount, b.customer_pay_asset ?? custAsset)} ${displayAsset(b.customer_pay_asset ?? custAsset)} · fee ${(b.customer_fee_bps / 100).toFixed(2)}%`
    + (b.rate_to_aed ? ` · rate ${fmtRateExact(b.rate_to_aed)} AED each` : '');
  // A PRESS THE BOOKS HAVE NOT ANSWERED, or a delivery that has STARTED, is shown instead of the form: either is finished with its own figures — never the calculator's,
  // whose suggestion is worked from a box that already holds this delivery's own balance leg.
  if (!held && reconciling) return <Reconciling error={readError} onRetry={onRetry} />;
  if (held || startedBody) {
    const b = held ? held.body : startedBody!;
    const sending = held?.state === 'sending';
    return (
      <div className="space-y-2">
        <StateLine
          tone={sending ? 'neutral' : 'warn'}
          more={held
            ? (sending ? undefined : 'The books did not answer this delivery, so part of it may be in the books. Its figures are fixed: sending it again finishes it, and what is already posted is not posted twice.')
            : 'The books already hold part of this delivery. It is finished with the figures it started with; other figures are refused.'}
        >
          {held ? (sending ? 'Posting this delivery…' : 'No answer — send it again; it counts once.') : 'A delivery was started — finish it with its own figures.'}
        </StateLine>
        <p className="text-meta text-tg-hint">{figures(b)}</p>
        <button className="btn-primary w-full text-body py-2.5 lg:py-1.5" disabled={sending} onClick={held ? sendHeldAgain : finishStarted}>
          {sending ? 'Posting…' : held ? 'Send again' : 'Finish delivery'}
        </button>
        {/* A held delivery the books never received can be dropped (two taps; nothing is sent): the books refuse any other figures for one that has STARTED, and then show it as such. */}
        {held && !sending && (
          <button type="button" className={`btn-link w-full text-body lg:text-meta max-lg:py-2 ${discarding ? 'text-neg' : 'text-tg-hint'}`}
            onClick={() => { if (discarding) { setDiscarding(false); presses.drop('delivery', dealId, held.id); } else setDiscarding(true); }}>
            {discarding ? 'Tap again to discard' : 'Discard it'}
          </button>
        )}
        {/* The books looked at the Finish and refused it (the month is closed, the cheque moved on): the press is over, the recorded figures stay, and the reason is said. */}
        {notSaved && <StateLine tone="bad" more={NOT_SAVED_MORE}>{notSaved}</StateLine>}
        {!held && submit.isError && <ServerLine error={submit.error} />}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* A delivery posted before 26 Oct kept only its balance: say it, and the form stays (the route refuses any other balance). */}
      {started && !startedBody && started.balance && (
        <StateLine tone="warn" more="The route refuses any other balance for a delivery that has started.">Delivery started: balance {fmtAmount(started.balance.amount, started.balance.asset)} {displayAsset(started.balance.asset)} — deliver that.</StateLine>
      )}
      {/* The fee in percent with the bps beside it — the bps, not the typed
          percent, are what the cheque is charged at (a 0.655% that becomes
          66 bps says so here). */}
      <FeePctField label="Customer fee %" value={feePct} onChange={setFeePct} className={FEE_FIELD_WIDTH} />
      {/* What the customer owes, once (21 Oct 2026 audit): the rate the deposit
          is valued at is the Rate box right below, and the closing line under
          the boxes no longer says owes / rate again. */}
      {/* 22 Oct 2026 sweep: the one 22-word line is two — what he owes and how it adds up (the bps are in the box above),
          then the deposit and what is still owed. WITH A COIN DEPOSIT AND NO RATE the deposit is not valued, so "still owed"
          would be the whole cheque (it was printed as 124,444,443.56 beside "enter the rate to value it"): it waits for the rate. */}
      <p className="text-meta text-tg-hint">
        Owes {fmtAmount(owedAed, 'AED')} AED = face {fmtAmount(face, 'AED')} + fee {fmtAmount(feeAmt, 'AED')}
      </p>
      <p className="text-meta text-tg-hint">
        {coin && !(rateToAed > 0)
          ? <>deposit {fmtAmount(deposit, custAsset)} {displayAsset(custAsset)} — enter the rate to value it</>
          : <>deposit {fmtAmount(deposit, custAsset)} {displayAsset(custAsset)}{coin ? <> ≈ {fmtAmount(depositAed, 'AED')} AED</> : null}{boxesRead ? <> · still owed {fmtAmount(stillAed, 'AED')} AED</> : null}</>}
      </p>
      {/* THE BOX THAT MOVES SAYS SO (12 Sept 2026): the one the calculator
          fills carries "auto" as its label hint and muted figures. The
          calculator decides (math.isDerived / math.rateIsDerived) — this
          screen never works it out itself. Labelled Amount / Rate / Total in
          AED, as stage 2 (§5.4). Prefilled with what is still owed after the
          deposit and anything already on account (sweep item 17): 0 when
          they paid in full before handover.
          RATE ABOVE AMOUNT (21 Oct 2026 audit): the Amount's hint said "rate
          first" while the Rate sat below it. The same three fields, the same
          handlers — only the order on the page changed. */}
      {coin && (
        <Field label="Rate" hint={math.rateIsDerived ? `${math.rateWords} · auto` : math.rateWords}>
          <input className={`input w-full${math.rateIsDerived ? ' text-tg-hint' : ''}`} value={math.rate} onChange={(e) => math.setRate(e.target.value)} inputMode="decimal" aria-label={`Rate, ${math.rateWords}`} />
        </Field>
      )}
      {/* A BOX NOT READ SUGGESTS NOTHING, and says so here as a fact of this field — the header's line owns the failure and its Try
          again (one failure, one message). A first read still waiting says nothing: the header shows the waiting line. */}
      <Field label="Amount" hint={math.isDerived(custAsset) ? 'auto' : coin && !(rateToAed > 0) ? 'rate first' : boxesFailed ? 'not suggested' : undefined}>
        <div className="flex gap-2">
          {/* The hint says "rate first" while the rate is empty; the box's
              placeholder stays empty — a placeholder is an example or nothing
              (§7 rule 5). */}
          <input className={`input flex-1${math.isDerived(custAsset) ? ' text-tg-hint' : ''}`} value={shownAmount(amount, custAsset, math.isDerived(custAsset), editing === 'amount')} onFocus={() => setEditing('amount')} onBlur={() => setEditing(null)} onChange={(e) => { setTouched(true); math.setAmount(custAsset, e.target.value); }} inputMode="decimal"
            aria-label={`Balance in ${displayAsset(custAsset)}`} />
          <AssetBox asset={custAsset} />
        </div>
        {/* "0 is fine if already paid in full" — said where it matters, once a
            0 stands in the box (21 Oct 2026 audit: it sat under an empty box,
            and the button beneath it read "nothing owed" before anything was
            typed). About this screen carries the rule for the rest. */}
        {typedZero && <p className="text-meta text-tg-hint mt-1">0 is fine if already paid in full</p>}
      </Field>
      {coin && (
        <Field label="Total in AED" hint={math.isDerived('AED') ? 'auto' : undefined}>
          <div className="flex gap-2">
            <input className={`input flex-1${math.isDerived('AED') ? ' text-tg-hint' : ''}`} value={shownAmount(math.amountOf('AED'), 'AED', math.isDerived('AED'), editing === 'aed')} onFocus={() => setEditing('aed')} onBlur={() => setEditing(null)} onChange={(e) => { setTouched(true); math.setAmount('AED', e.target.value); }} inputMode="decimal" aria-label="Total in AED" />
            <AssetBox asset="AED" />
          </div>
        </Field>
      )}
      {prepaid > 0 && (
        <p className="text-meta text-tg-hint">
          Already paid {fmtAmount(prepaid, custAsset)} {displayAsset(custAsset)} beyond the deposit — taken off the balance.
        </p>
      )}
      {coin && rateToAed > 0 && amountOk && (
        <>
          <p className="text-meta text-tg-hint">
            {/* TO THE FILS. AED renders at whole units everywhere else on the desk,
                which made this line compare two figures up to 0.49 apart and print
                them as the same number, and show a one-fils residue as "≈0" — at
                exactly the resolution this line exists to check (3 Sept audit).
                The books' eight-place rate can value a crore of INR a dirham
                apart from the box above; About this screen says why.
                21 Oct 2026 audit: the rate and the owed figure are no longer
                said again here — the Rate box and the line above say them; this
                line is the verdict, and the verdict stays whole.
                "Closes to zero" is said only when EVERY asset of the box projects to ZERO at the ledger's precision; otherwise the line under says what stays. */}
            deposit + balance = {fmtAmount(paidAed, 'AED', 2)} AED
            {boxesRead && staysOpen.length === 0 && <> · the customer&rsquo;s box closes to zero</>}
          </p>
          {/* WHAT STAYS OPEN in their box once this posts: the AED and the coin the delivery leaves, and anything else the box held.
              One line — the first balance and a count of the rest, so a phone keeps it on one row; every balance, why, and what the
              two are worth together behind Details — a valuation, never a closed box. */}
          {boxesRead && staysOpen.length > 0 && (
            <StateLine
              tone="warn"
              more={<>
                The delivery converts the deposit and this balance only; every other balance in their box stays as it is. After it:{' '}
                {staysOpen.map((a, i) => <span key={a}>{i > 0 ? ' · ' : ''}{say(a)}</span>)}.
                {(afterBox.AED ?? 0) > 0 && (afterBox[coinLine] ?? 0) < 0 && aedHalfEven(-(afterBox[coinLine] ?? 0) * rateToAed) > 0
                  ? <> The <span className="whitespace-nowrap">{ledgerFigure(-(afterBox[coinLine] ?? 0), coinLine)} {displayAsset(coinLine)}</span> they hold is worth <span className="whitespace-nowrap">{fmtAmount(aedHalfEven(-(afterBox[coinLine] ?? 0) * rateToAed), 'AED', 2)} AED</span> at this rate: a valuation, not a netting.</>
                  : null}
              </>}
            >
              {fit?.small ? <span className="text-meta">{afterLine}</span> : afterLine}
            </StateLine>
          )}
        </>
      )}
      {/* THE REFUSAL OPENS ABOVE THE BUTTON, FULL WIDTH, with the route's own
          sentence (both figures and the balance to deliver with) and the one
          way past it. Nothing was written when it answered. */}
      {alreadyPaid && (
        <div className="rounded-lg bg-warn-bg p-2.5 space-y-2">
          <StateLine tone="warn" more={errorWords(submit.error)}>
            Already paid on account — delivering this would take it twice.
          </StateLine>
          {/* Two full-width buttons on the phone, a wrapping row at lg+ —
              side by side at 375px the long second label folded into a
              three-line button (19 Sept 2026). */}
          <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap">
            <button type="button" className="btn-secondary w-full lg:w-auto text-body lg:text-meta py-2.5 lg:py-1 px-3 lg:px-2" onClick={() => {
              // The route names the figure it will accept (`still_owed`); use
              // THAT, and re-read the box so the prefill cannot come back from
              // a stale balance and be refused a second time (review, 14 Sept 2026).
              const owedNow = (submit.error as { body?: { still_owed?: number } } | null)?.body?.still_owed;
              qc.invalidateQueries({ queryKey: ['parties', 'balances'] });
              qc.invalidateQueries({ queryKey: ['party', customerSlug] });
              if (typeof owedNow === 'number' && Number.isFinite(owedNow)) { setTouched(true); math.preset({ asset: custAsset }, String(owedNow)); }
              else setTouched(false);
              submit.reset();
            }}>
              Use the balance still owed
            </button>
            <button type="button" className="btn-secondary w-full lg:w-auto text-body lg:text-meta py-2.5 lg:py-1 px-3 lg:px-2 text-neg" disabled={submit.isPending}
              onClick={() => post(true)}>
              {submit.isPending ? 'Posting…' : `They are paying ${fmtAmount(Number(amount) || 0, custAsset)} ${displayAsset(custAsset)} again — deliver anyway`}
            </button>
          </div>
        </div>
      )}
      <button
        className="btn-primary w-full text-body py-2.5 lg:py-1.5"
        disabled={submit.isPending || amount.trim() === '' || Number(amount) < 0 || Number.isNaN(Number(amount)) || (coin && !(rateToAed > 0))}
        onClick={() => post()}
      >
        {deliverWords(submit.isPending, amount)}
      </button>
      {notSaved && <StateLine tone="bad" more={NOT_SAVED_MORE}>{notSaved}</StateLine>}
      {submit.isError && !alreadyPaid && <ServerLine error={submit.error} />}
    </div>
  );
}

// ── Void inline ─────────────────────────────────────────────────────────
// WHAT A VOID DOES, AND WHAT IT LEAVES BEHIND. The route records the state
// change and posts nothing — every leg already on the ledger stays exactly
// where it is. The old sentence said "reverse manually via Adjust Balance" for
// all of it, which is wrong twice over: it never named WHICH legs are open,
// and Adjust balance is a book correction, while handing the customer their
// deposit back is money leaving the desk and belongs on Record money. The
// figures are stated before the confirm because afterwards the deal screen
// shows only "voided". The Adjust-balance sentence lives in About this screen.
export function VoidInline({ dealId, deposit, depositAsset, sellerPaidAed, onDone }: {
  dealId: string; deposit: number; depositAsset: string; sellerPaidAed: number; onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const submit = useMutation({
    mutationFn: () => api.voidMc(dealId, reason.trim()),
    onSuccess: () => { tg()?.HapticFeedback.notificationOccurred('success'); onDone(); setOpen(false); },
    onError:   () => tg()?.HapticFeedback.notificationOccurred('error'),
  });

  if (!open) {
    return (
      <button className="btn-link text-meta text-neg" onClick={() => setOpen(true)}>
        Void this MC
      </button>
    );
  }
  return (
    <section className="card border-neg/40 space-y-2">
      {/* THE GUARD IS ONE AMBER LINE (22 Oct 2026 sweep): a void moves no money. What is still held, and where to give it
          back, are the two lines under it — each a fact and its door, in few words. */}
      <StateLine tone="warn">Voiding moves no money — what is posted stays posted.</StateLine>
      <div className="text-meta text-tg-hint space-y-1">
        {deposit > 0 && (
          <p>
            Deposit {fmtAmount(deposit, depositAsset)} {displayAsset(depositAsset)} still held — return it from Record money (Out).
          </p>
        )}
        {sellerPaidAed > 0 && (
          <p>
            Seller took {fmtAmount(sellerPaidAed, 'AED')} AED-equivalent — recover it from Record money (In).
          </p>
        )}
        {deposit <= 0 && sellerPaidAed <= 0 && <p>No money has moved on this deal — nothing to give back.</p>}
      </div>
      <Field label="Reason" hint="required">
        <textarea className="input h-16" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason" />
      </Field>
      {/* Cancel is a link, so the two actions are not the same shape (§5.4). */}
      <div className="flex items-center justify-between gap-2">
        <button type="button" className="btn-link text-meta max-lg:py-2" onClick={() => setOpen(false)}>Cancel</button>
        <button
          type="button"
          className="btn-secondary text-neg text-body py-2.5 lg:py-1.5 max-lg:flex-1"
          disabled={submit.isPending || !reason.trim()}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? 'Voiding…' : 'Confirm void'}
        </button>
      </div>
      {submit.isError && <ServerLine error={submit.error} />}
    </section>
  );
}
